/**
 * useTutorApplicationWrittenState — un entretien écrit de la candidature : la motivation (étape 2)
 * ou la pédagogie (étape 4). Les questions de l'ancien `InterviewScreen`, posées par
 * le champion de l'étape, l'envoi (`submitTestAnswers`), et la fin d'étape (`getCongratsText`).
 */
import { useNavigation } from "@react-navigation/native"
import { useRef, useState } from "react"
import { useSelector } from "react-redux"

import { tutorsService } from "../../../../../../data/services/tutorsService"
import { type TutorApplicationVideoQuestionPageProps } from "../../../../../../design-system/pages/TutorApplicationVideoQuestionPage/TutorApplicationVideoQuestionPage"
import {
    applicationStepChampion,
    interviewStepDoneText,
} from "../../../../adapters/tutor/Application/tutorApplicationAdapter"
import {
    type ApplicationQuestion,
    MOTIVATION_QUESTIONS,
    PEDAGOGY_QUESTIONS,
    toTestAnswersPayload,
} from "../../../../adapters/tutor/Application/tutorApplicationQuestions"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useTutorApplicationForm } from "./useTutorApplicationForm"
import { APPLICATION_SUBMIT_FALLBACK } from "./useTutorApplicationProfileState"
import { applicationAsker, selectCandidate } from "./candidateState"

export const INTERVIEW_EXIT_MESSAGE =
    "Si tu quittes cet écran avant d'avoir soumis ta réponse, c'est comme si tu avais passé la question."

const NO_QUESTIONS: ApplicationQuestion[] = []

export function questionsForCategory(category: string): ApplicationQuestion[] {
    if (category === "motivation") return MOTIVATION_QUESTIONS
    if (category === "educational") return PEDAGOGY_QUESTIONS
    return NO_QUESTIONS
}

export function useTutorApplicationWrittenState(category: string, stepNum: number) {
    const navigation = useNavigation() as unknown as { goBack: () => void }
    const user = useSelector(selectCandidate)
    const [serverError, setServerError] = useState<string | null>(null)
    const markDoneRef = useRef<() => void>(() => undefined)
    const champion = applicationStepChampion(category)

    const mutation = useApiMutation({
        service: tutorsService.submitTestAnswers,
        gesture: "application.answers",
        surface: "tutor_application",
        fallback: APPLICATION_SUBMIT_FALLBACK,
        onSuccess: () => markDoneRef.current(),
        onError: (message) => setServerError(message),
    })

    const form = useTutorApplicationForm({
        questions: questionsForCategory(category),
        formId: "interview-step",
        entryId: user?.id,
        askedBy: applicationAsker(user, stepNum, champion),
        exitMessage: INTERVIEW_EXIT_MESSAGE,
        alwaysConfirmExit: true,
        submit: (state) => {
            setServerError(null)
            return mutation.start({}, toTestAnswersPayload(state.values, state.precisions))
        },
        submitting: mutation.requesting,
        serverError,
        stepDone: {
            champion,
            text: interviewStepDoneText(stepNum, user?.profile?.relevant_subjects),
        },
    })
    markDoneRef.current = form.markDone

    const empty: TutorApplicationVideoQuestionPageProps = {
        question: null,
        alreadySeen: true,
        phase: { kind: "thinking", secondsLeft: 0 },
        onStart: () => undefined,
        onFinish: () => undefined,
        onContinue: () => navigation.goBack(),
        onClose: () => navigation.goBack(),
    }

    return { ...form, empty }
}
